import { AlertCircle, CheckCircle2, GitMerge, Loader2, RefreshCw, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { type FileUploadEntry, useFolderUpload } from "@/hooks/use-files";

interface FolderUploadZoneProps {
  projectId: string;
  folderId: string;
  children: React.ReactNode;
}

export function FolderUploadZone({ projectId, folderId, children }: FolderUploadZoneProps) {
  const { uploads, startUpload, retryUpload, resolveVersion, clearCompleted } = useFolderUpload(
    projectId,
    folderId,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault();
    dragCounterRef.current++;
    if (dragCounterRef.current === 1) setIsDragging(true);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) startUpload(dropped);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) startUpload(selected);
    e.target.value = "";
  }

  function openRenameDialog(id: string, originalName: string) {
    setRenamingId(id);
    setRenameValue(originalName);
  }

  function confirmRename() {
    if (!renamingId) return;
    resolveVersion(renamingId, "rename", renameValue.trim() || undefined);
    setRenamingId(null);
    setRenameValue("");
  }

  const hasPending = uploads.some(
    (u) =>
      u.status === "pending" ||
      u.status === "uploading" ||
      u.status === "duplicate" ||
      u.status === "error",
  );

  return (
    <div
      className="relative flex flex-col h-full overflow-hidden"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-md border-2 border-dashed border-primary bg-primary/5 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2 text-primary">
            <Upload className="h-10 w-10" />
            <p className="text-sm font-medium">Drop files to upload</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between border-b px-5 py-2.5 shrink-0">
        <span className="text-xs text-muted-foreground">
          Drag files here or use the button to upload
        </span>
        <div className="flex items-center gap-2">
          {uploads.length > 0 && !hasPending && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearCompleted}>
              Clear
            </Button>
          )}
          <Button size="sm" className="h-7" onClick={() => fileInputRef.current?.click()}>
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            Upload Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFileSelect}
            aria-label="Select files to upload"
          />
        </div>
      </div>

      {/* Upload progress panel */}
      {uploads.length > 0 && (
        <div className="shrink-0 border-b bg-muted/20 px-5 py-3 space-y-2 max-h-48 overflow-y-auto">
          {uploads.map((u) => (
            <UploadRow
              key={u.id}
              upload={u}
              onRetry={retryUpload}
              onVersion={(id) => resolveVersion(id, "version")}
              onRename={(id) => openRenameDialog(id, u.file.name)}
            />
          ))}
        </div>
      )}

      {/* File list */}
      <div className="flex-1 overflow-auto p-5">{children}</div>

      {/* Rename dialog */}
      <Dialog
        open={renamingId !== null}
        onOpenChange={(open) => {
          if (!open) setRenamingId(null);
        }}
      >
        <DialogContent className="w-[50vw] max-w-[50vw] sm:max-w-[50vw]">
          <DialogHeader>
            <DialogTitle>Rename file</DialogTitle>
            <DialogDescription>
              Enter a new name so this file can be uploaded alongside the existing one.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmRename()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenamingId(null)}>
              Cancel
            </Button>
            <Button onClick={confirmRename} disabled={!renameValue.trim()}>
              Upload with new name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface UploadRowProps {
  upload: FileUploadEntry;
  onRetry: (id: string) => void;
  onVersion: (id: string) => void;
  onRename: (id: string) => void;
}

function UploadRow({ upload, onRetry, onVersion, onRename }: UploadRowProps) {
  const { id, file, status, progress, errorMessage } = upload;

  return (
    <div className="flex items-center gap-3 rounded-md bg-background px-3 py-2 text-sm shadow-sm border">
      <StatusIcon status={status} progress={progress} />

      <div className="min-w-0 flex-1">
        <p className="truncate font-medium leading-tight">{file.name}</p>
        {status === "uploading" && <Progress value={progress} className="mt-1 h-1.5" />}
        {status === "error" && errorMessage && (
          <p className="text-xs text-destructive mt-0.5">{errorMessage}</p>
        )}
        {status === "duplicate" && (
          <p className="text-xs text-amber-600 mt-0.5">
            A file with this name already exists in this folder.
          </p>
        )}
      </div>

      {status === "uploading" && (
        <span className="text-xs text-muted-foreground shrink-0">{progress}%</span>
      )}
      {status === "success" && (
        <Badge variant="secondary" className="shrink-0 text-xs">
          Done
        </Badge>
      )}
      {status === "error" && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 shrink-0 px-2 text-xs"
          onClick={() => onRetry(id)}
        >
          <RefreshCw className="mr-1 h-3 w-3" />
          Retry
        </Button>
      )}
      {status === "duplicate" && (
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onVersion(id)}
          >
            <GitMerge className="mr-1 h-3 w-3" />
            New version
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onRename(id)}
          >
            Rename
          </Button>
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status, progress }: { status: FileUploadEntry["status"]; progress: number }) {
  if (status === "uploading") {
    return (
      <div className="relative h-5 w-5 shrink-0">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="sr-only">{progress}%</span>
      </div>
    );
  }
  if (status === "success") return <CheckCircle2 className="h-5 w-5 shrink-0 text-green-500" />;
  if (status === "error") return <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />;
  if (status === "duplicate") return <X className="h-5 w-5 shrink-0 text-amber-500" />;
  return <Upload className="h-5 w-5 shrink-0 text-muted-foreground" />;
}
